import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FormStats from 'components/ProspectDashboard/FormStats'
import AdditionalQuestionInfo from 'components/ProspectDashboard/AdditionalQuestionInfo'
import QuestionsStatus from 'components/DueDiligence/QuestionsStatus'
import moment from 'moment/moment'
import { cloneDeep } from 'lodash'
import { DialogConfirm } from 'components'
import { setFormUUID } from 'redux/reducers/form'
import jwtDecode from 'jwt-decode'
import AutomaticQuestions from 'components/ProspectDashboard/AutomaticQuestions'
import { toast } from 'react-toastify'
import {
  getApplicationOpenQuestions,
  getExtraAutomaticQuestions,
  getOngoingApplication,
} from 'services/apiKYP'

import styles from './styles'

const initialValues = {
  address: false,
  social_networks: false,
  financial_activities: false,
  environment_related_activities: false,
  external_activities: false,
  national_product_offering_activities: false,
  international_product_offering_activities: false,
  representation_activities: false,
  automation_activities: false,
  kinship_xp: false,
  pep_relationship: false,
  attorney_activities: false,
  third_party_investments: false,
  court_lawsuit_cvm_bacen_sfn: false,
  environmental_court_lawsuit: false,
  criminal_court_lawsuit: false,
  negative_media: false,
  restrictive_list: false,
  relation_with_another_companies: false,
  terms_agreement: false,
}

const ProspectDashboard = () => {
  const classes = styles()

  const dispatch = useDispatch()
  
  const numberOfQuestions = Object.keys(initialValues).length

  const token = useSelector((state) => state.auth.access_token)
  const full_name = jwtDecode(token).extras.name.split(' ')
  const username = full_name[0]

  const [filled, setFilled] = useState(false)
  const [pendingAnswers, setPendingAnswers] = useState(0)
  const [creationDate, setCreationDate] = useState()
  const [expireDate, setExpireDate] = useState()
  const [finishedDate, setFinishedDate] = useState()
  const [applicationUuid, setApplicationUuid] = useState('')
  const [filledQuestions, setFilledQuestions] = useState(initialValues)
  const [openAlert, setOpenAlert] = useState(false)
  const [message, setMessage] = useState('')
  const [automaticQuestions, setAutomaticQuestions] = useState([])
  const [openQuestions, setOpenQuestions] = useState([])

  const initializeState = (data) => {
    const { form } = data
    const questionsAnswered = form?.answers?.map(
      (answer) => answer.answer_object.question_name,
    )
    const questionsFilled = cloneDeep(initialValues)
    questionsAnswered.forEach((question) => {
      questionsFilled[question] = true
    })

    const created_at = moment(data.created).format('DD/MM/YYYY')
    const expires_at = moment(data.expires_at).format('DD/MM/YYYY')
    const finished_at = moment(form.finished_at).format('DD/MM/YYYY HH:mm:ss')

    setCreationDate(created_at)
    setExpireDate(expires_at)
    setFinishedDate(finished_at)

    setApplicationUuid(data?.uuid)

    setFilledQuestions(questionsFilled)

    setPendingAnswers(numberOfQuestions - form?.answers?.length)

    setFilled(form.filled)
  }

  const handleCloseDialog = () => {
    setOpenAlert(false)
  }

  const handleLoadApplicationData = () => {
    getOngoingApplication(token)
      .then((response) => {
        dispatch(setFormUUID(response?.data?.form?.uuid))
        if (response?.data?.form) initializeState(response?.data)
      })
      .catch(() => {
        toast.error("Houve um erro ao obter os dados da aplicação")
      })
  }

  const handleLoadAutomaticQuestions = async () => {
    if (applicationUuid !== '') {
      getExtraAutomaticQuestions(token, applicationUuid)
        .then((response) => {
          setAutomaticQuestions(response.data)
        })
        .catch(() => {
          toast.error('Houve um erro ao obter os esclarecimentos adicionais')
        })
    }
  }
  
  const handleLoadOpenQuestions = async () => {
    if (applicationUuid !== '') {
      getApplicationOpenQuestions(token, applicationUuid)
        .then((response) => {
          setOpenQuestions(response.data)
        })
        .catch(() => {
          toast.error('Houve um erro ao obter as perguntas adicionais')
        })
    }
  }

  useEffect(() => {
    handleLoadApplicationData()
  }, [])

  useEffect(() => {
    handleLoadAutomaticQuestions()
    handleLoadOpenQuestions()
  }, [applicationUuid])

  useEffect(() => {
    if (!filled) {
      setMessage(
        `Você possui pendências de preenchimento do formulário de inscrição. ${
          pendingAnswers === 0
            ? 'Todas as questões foram preenchidas, mas o formulário não foi enviado.'
            : `Ainda há ${pendingAnswers} questões a serem preechidas até o dia ${expireDate}.`
        }`,
      )
    } else if (!automaticQuestions.every((question) => question.answered)) {
      setMessage(
        `Você possui esclarecimentos adicionais pendentes, por favor responda-os para que possamos dar prosseguimento ao processo.`,
      )
    } else if (!openQuestions.every((question) => question.answered)) {
      setMessage(
        `Você possui perguntas adicionais pendentes, por favor responda-as para que possamos dar prosseguimento ao processo.`,
      )
    } else {
      setMessage(
        `Você já concluiu o preenchimento do formulário. Para rever suas respostas, clique no botão 'VISUALIZAR RESPOSTAS'.`,
      )
    }
    setOpenAlert(true)
  }, [filled, expireDate, pendingAnswers])

  return (
    <div className={classes.container}>
      <div className={classes.wrapper}>
        <div className={classes.leftSide}>
          <FormStats
            filled={filled}
            pendingNumber={pendingAnswers}
            questionsNumber={numberOfQuestions}
            creationDate={creationDate}
            expiresAt={expireDate}
            finishedAt={finishedDate}
          />
          {automaticQuestions.length > 0 ? (
            <AutomaticQuestions questions={automaticQuestions} />
          ) : null}
          <AdditionalQuestionInfo questions={openQuestions} reloadData={handleLoadOpenQuestions} />
        </div>
        <QuestionsStatus questions={filledQuestions} />
      </div>
      <DialogConfirm
        open={openAlert}
        onCancel={handleCloseDialog}
        btnCancelText='OK'
        title={`Bem-vindo, ${username}!`}
      >
        <p className={classes.justified}>{message}</p>
        <br />
        {filled
          ? ''
          : `Clique no botão 'PREENCHER FORMULÁRIO' ${
              pendingAnswers === 0 ? 'e realize o envio' : 'para continuar'
            }.`}
      </DialogConfirm>
    </div>
  )
}

export default ProspectDashboard
